import { BanIcon, EyeIcon } from 'lucide-react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { FieldHeader } from '@/components/atoms/FieldHeader/FieldHeader';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_DIALOG_PREVIEW } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import { AutocompleteOption, Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import { pushModal } from '@/redux/modals/actions';

import { NodeOptionLabel } from './NodeOptionLabel';
import { NodeOption, PreferencesForm } from './preferencesForm';

interface StartingDialogFieldProps {
  detailedOptions: NodeOption[];
  canWrite: boolean;
}

export const StartingDialogField = ({ detailedOptions, canWrite }: StartingDialogFieldProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hash } = useLocation();
  const { control } = useFormContext<PreferencesForm>();
  const selectedNodeId = useWatch({ control, name: 'starting_node_id' });

  const finalOptions: AutocompleteOption<NodeOption>[] = detailedOptions.map((option) => ({
    value: option.value,
    label: option.label,
    category: option.dialog_name,
    data: option,
  }));

  const pinnedOptions: AutocompleteOption<NodeOption>[] = selectedNodeId
    ? [{ value: '', label: t('common.none') }]
    : [];

  const selectedOption = detailedOptions.find((option) => option.value === selectedNodeId) ?? null;

  const handlePreview = () => {
    if (!selectedOption) {
      return;
    }
    const { dialog_id, dialog_name } = selectedOption;
    dispatch(pushModal(MODAL_DIALOG_PREVIEW, { name: dialog_name, dialogId: dialog_id }));
  };

  const renderNode = (option: AutocompleteOption<NodeOption>, side: 'top' | 'right') => (
    <NodeOptionLabel
      label={option.label}
      nodeType={option.data?.node_type}
      icon={
        option.value === '' ? <BanIcon size={16} className="text-foreground-muted" /> : undefined
      }
      side={side}
    />
  );

  return (
    <div className="flex flex-col gap-2">
      <FieldHeader title={t('ai_agents.conversation_start.title')} />

      <div className="flex items-start gap-2">
        {/* Node Selector */}
        <div className="min-w-0 flex-1">
          <Controller
            name="starting_node_id"
            control={control}
            render={({ field }) => (
              <Autocomplete<NodeOption>
                options={finalOptions}
                pinnedOptions={pinnedOptions}
                value={field.value}
                onChange={field.onChange}
                placeholder={t('ai_agents.conversation_start.placeholder')}
                searchPlaceholder={t('ai_agents.conversation_start.search_placeholder')}
                disabled={!canWrite}
                renderOption={(option) => renderNode(option, 'right')}
                renderValue={(option) => renderNode(option, 'top')}
                autoFocus={hash === '#conversation-start'}
              />
            )}
          />
        </div>

        {/* Preview Button */}
        {!!selectedOption?.dialog_id && (
          <InfoTooltip content={t('ai_agents.conversation_start.preview_tooltip')} side="top">
            <Button
              type="button"
              variant="outline-neutral"
              size="icon-lg"
              aria-label={t('ai_agents.conversation_start.preview_tooltip')}
              onClick={handlePreview}
            >
              <EyeIcon size={16} />
            </Button>
          </InfoTooltip>
        )}
      </div>

      {/* Helper Text */}
      <span className="text-sm leading-5 font-normal text-foreground-muted">
        {t('ai_agents.conversation_start.helper_text')}
      </span>
    </div>
  );
};
